// The client's own details.
//
// Everything the firm needs to identify this person to the ATO, filled in by
// the person who actually knows it.
//
// ---------------------------------------------------------------------------
// The tax file number cannot be read back
// ---------------------------------------------------------------------------
// It can be entered and replaced, never displayed. The field shows whether one
// is on file and its last three digits, the way a card ends 4242, so somebody
// who has typed two different numbers can tell which one stuck.
//
// That is deliberate. If somebody steals this session, the question is what
// they can take. A readable TFN means they take the TFN; this way the worst
// they can do is overwrite it, which is vandalism, visible in the audit log,
// and fixable. It costs the client nothing — nobody needs to read their own
// tax file number off a screen.

import { checkDob, checkTfn } from '@myshoebox/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { ApiError, api } from '../api/client.js';
import type { ClientProfile } from '../api/client.js';

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

export interface YourDetailsProps {
  onDone: () => void;
  /** Re-reads the header banner once something is saved. */
  onSaved?: () => void;
}

export function YourDetails({ onDone, onSaved }: YourDetailsProps): JSX.Element {
  const [profile, setProfile] = useState<ClientProfile | undefined>(undefined);
  const [form, setForm] = useState<Record<string, string>>({});
  const [tfn, setTfn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const p = await api.profile();
        setProfile(p);
        setForm({
          givenNames: p.givenNames ?? '',
          familyName: p.familyName ?? '',
          mobile: p.mobile ?? '',
          addressLine1: p.addressLine1 ?? '',
          addressLine2: p.addressLine2 ?? '',
          suburb: p.suburb ?? '',
          state: p.state ?? '',
          postcode: p.postcode ?? '',
          dob: p.dob ?? '',
        });
      } catch {
        setError('Could not load your details.');
      }
    })();
  }, []);

  function set(field: string, value: string): void {
    setForm((f) => ({ ...f, [field]: value }));
    setSaved(false);
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;

    // Checked here before sending, using the same functions the server uses.
    // Catching a mistyped TFN now, while the client is looking at it, is far
    // kinder than a failed lodgement in six weeks.
    if (tfn.trim()) {
      const result = checkTfn(tfn);
      if (!result.ok) {
        setError(result.message);
        return;
      }
    }
    if (form.dob) {
      const result = checkDob(form.dob);
      if (!result.ok) {
        setError(result.message);
        return;
      }
    }

    setBusy(true);
    setError(undefined);

    try {
      await api.updateProfile({ ...form, ...(tfn.trim() ? { tfn } : {}) });
      const fresh = await api.profile();
      setProfile(fresh);
      // Cleared so the number is not sitting in the DOM, and so a second save
      // does not silently rewrite it.
      setTfn('');
      setSaved(true);
      onSaved?.();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!profile) {
    return (
      <div className="details">
        <p className="details__loading">Loading your details…</p>
      </div>
    );
  }

  return (
    <div className="details">
      <header className="details__head">
        <button type="button" className="btn btn--soft" onClick={onDone}>
          ← Back
        </button>
        <h1>Your details</h1>
        <p>
          Your accountant needs these before your return can be lodged. They are encrypted, and only
          your accountant can see them.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="details__form">
        <fieldset className="details__group">
          <legend>Your name</legend>
          <div className="details__row">
            <label className="field">
              <span className="field__label">Given names</span>
              <input
                value={form.givenNames ?? ''}
                onChange={(e) => set('givenNames', e.target.value)}
                autoComplete="given-name"
              />
            </label>
            <label className="field">
              <span className="field__label">Family name</span>
              <input
                value={form.familyName ?? ''}
                onChange={(e) => set('familyName', e.target.value)}
                autoComplete="family-name"
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="details__group">
          <legend>How to reach you</legend>
          <div className="details__row">
            <label className="field">
              <span className="field__label">Mobile</span>
              <input
                type="tel"
                value={form.mobile ?? ''}
                onChange={(e) => set('mobile', e.target.value)}
                autoComplete="tel"
                placeholder="0412 345 678"
              />
            </label>
            <label className="field">
              <span className="field__label">Email</span>
              {/* Not editable here: it is how you sign in, so changing it is a
                  conversation with the practice rather than a text field. */}
              <input value={profile.email} disabled />
            </label>
          </div>
        </fieldset>

        <fieldset className="details__group">
          <legend>Your address</legend>
          <label className="field">
            <span className="field__label">Street address</span>
            <input
              value={form.addressLine1 ?? ''}
              onChange={(e) => set('addressLine1', e.target.value)}
              autoComplete="address-line1"
              placeholder="123 Smith Street"
            />
          </label>
          <label className="field">
            <span className="field__label">
              Unit or building <span className="field__optional">optional</span>
            </span>
            <input
              value={form.addressLine2 ?? ''}
              onChange={(e) => set('addressLine2', e.target.value)}
              autoComplete="address-line2"
            />
          </label>
          <div className="details__row">
            <label className="field field--grow">
              <span className="field__label">Suburb</span>
              <input
                value={form.suburb ?? ''}
                onChange={(e) => set('suburb', e.target.value)}
                autoComplete="address-level2"
              />
            </label>
            <label className="field field--narrow">
              <span className="field__label">State</span>
              <select value={form.state ?? ''} onChange={(e) => set('state', e.target.value)}>
                <option value="">—</option>
                {STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field--narrow">
              <span className="field__label">Postcode</span>
              <input
                value={form.postcode ?? ''}
                onChange={(e) => set('postcode', e.target.value)}
                autoComplete="postal-code"
                inputMode="numeric"
                maxLength={4}
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="details__group details__group--sensitive">
          <legend>For the tax office</legend>
          <p className="details__note">
            The two things that must be right before anything can be lodged.
          </p>

          <div className="details__row">
            <label className="field">
              <span className="field__label">Date of birth</span>
              <input
                type="date"
                value={form.dob ?? ''}
                onChange={(e) => set('dob', e.target.value)}
                autoComplete="bday"
                max={new Date().toISOString().slice(0, 10)}
              />
            </label>

            <label className="field">
              <span className="field__label">
                Tax file number
                {profile.hasTfn && <span className="field__onfile">on file</span>}
              </span>
              <input
                value={tfn}
                onChange={(e) => {
                  setTfn(e.target.value);
                  setSaved(false);
                }}
                inputMode="numeric"
                autoComplete="off"
                placeholder={profile.hasTfn ? (profile.tfnHint ?? '•••  •••  •••') : '123 456 789'}
              />
              <span className="field__hint">
                {profile.hasTfn
                  ? 'Already recorded. Type a new one only if it needs changing — for your security we cannot show you the number we hold.'
                  : 'Nine digits. You will find it on a payslip or an ATO letter.'}
              </span>
            </label>
          </div>
        </fieldset>

        {error && <output className="details__error">{error}</output>}
        {saved && !error && <output className="details__saved">Saved.</output>}

        <div className="details__actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save my details'}
          </button>
        </div>
      </form>
    </div>
  );
}
